import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  FileText,
  CheckCircle,
  AlertCircle,
  MessageCircle,
  ArrowLeft,
} from 'lucide-react';
import { SEO } from '../components/common';
import { Button, FormInput, FormSelect, FormTextarea } from '../components/ui';
import {
  CONTACT_INFO,
  SERVICE_OPTIONS,
  GENDER_OPTIONS,
  TIME_SLOTS,
} from '../utils/constants';
import { validateAppointmentForm } from '../utils/validation';
import { bookAppointment, generateWhatsAppBookingLink } from '../services/api';

/**
 * Book Appointment page component
 */
const BookAppointmentPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    gender: '',
    service: '',
    date: '',
    time: '',
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [bookingId, setBookingId] = useState(null);

  // Get today's date for min date in date picker
  const today = new Date().toISOString().split('T')[0];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Validate on blur
    const validationErrors = validateAppointmentForm(formData);
    if (validationErrors[name]) {
      setErrors((prev) => ({ ...prev, [name]: validationErrors[name] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {}
    );
    setTouched(allTouched);

    // Validate all fields
    const validationErrors = validateAppointmentForm(formData);
    setErrors(validationErrors);

    // If there are errors, don't submit
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await bookAppointment(formData);
      setSubmitStatus('success');
      setBookingId(response.data.id);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookViaWhatsApp = () => {
    // Validate form first
    const validationErrors = validateAppointmentForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const allTouched = Object.keys(formData).reduce(
        (acc, key) => ({ ...acc, [key]: true }),
        {}
      );
      setTouched(allTouched);
      return;
    }

    // Get service name from options
    const selectedService = SERVICE_OPTIONS.find((s) => s.value === formData.service);
    const whatsAppLink = generateWhatsAppBookingLink({
      ...formData,
      service: selectedService?.label || formData.service,
    });
    window.open(whatsAppLink, '_blank');
  };

  // Success State
  if (submitStatus === 'success') {
    return (
      <>
        <SEO title="Appointment Booked" url="/book-appointment" />
        <section className="min-h-[70vh] flex items-center py-16">
          <div className="container-custom">
            <div className="max-w-lg mx-auto text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-neutral-900 mb-4">
                Appointment Request Submitted!
              </h1>
              <p className="text-neutral-600 mb-6">
                Thank you for booking with Mian Tufail Diagnostic Center. 
                We have received your appointment request and will contact you shortly 
                to confirm your booking.
              </p>
              
              {bookingId && (
                <div className="bg-neutral-50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-neutral-500 mb-1">Booking Reference</p>
                  <p className="font-mono font-semibold text-neutral-900">{bookingId}</p>
                </div>
              )}

              <div className="space-y-3">
                <Link to="/">
                  <Button variant="primary" fullWidth>
                    Back to Home
                  </Button>
                </Link>
                <a
                  href={CONTACT_INFO.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="outline" icon={MessageCircle} fullWidth>
                    Contact via WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Book MRI Appointment Online - Easy Scheduling"
        description="Book your MRI appointment online at Mian Tufail Diagnostic Center Kasur. Easy scheduling for Open MRI, Whole Body MRI, Brain MRI, Spine MRI & more, with routine reports delivered in 6-8 hours and emergency report delivery within 2 hours. Call +92 300 047 8311 or book via WhatsApp."
        keywords="book MRI appointment Kasur, schedule MRI scan online, MRI booking Pakistan, diagnostic center appointment, WhatsApp MRI booking, 6-8 hour MRI reports, emergency MRI report delivery, MRI scan near me"
        url="/book-appointment"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Book Appointment', url: '/book-appointment' },
        ]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-50 to-white py-16 md:py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <Link
              to="/"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              Book Your <span className="gradient-text">Appointment</span>
            </h1>
            <p className="text-lg text-neutral-600 leading-relaxed">
              Schedule your MRI scan at Mian Tufail Diagnostic Center. 
              Fill out the form below and we&apos;ll confirm your appointment.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {/* Error Message */}
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Something went wrong</p>
                  <p className="text-sm text-red-600">
                    Please try again or book via WhatsApp for immediate assistance.
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-8">
              {/* Personal Information */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-soft border border-neutral-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-primary-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-neutral-900">
                    Personal Information
                  </h2>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <FormInput
                    label="Full Name"
                    name="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.name}
                    touched={touched.name}
                    required
                    icon={User}
                  />
                  <FormInput
                    label="Phone Number"
                    name="phone"
                    type="tel"
                    placeholder="0300 1234567"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.phone}
                    touched={touched.phone}
                    required
                    icon={Phone}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <FormInput
                    label="Email Address"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.email}
                    touched={touched.email}
                    helperText="Optional - for appointment confirmation"
                    icon={Mail}
                  />
                  <FormSelect
                    label="Gender"
                    name="gender"
                    options={GENDER_OPTIONS}
                    placeholder="Select your gender"
                    value={formData.gender}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.gender}
                    touched={touched.gender}
                    required
                  />
                </div>
              </div>

              {/* Appointment Details */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-soft border border-neutral-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-primary-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-neutral-900">
                    Appointment Details
                  </h2>
                </div>

                <FormSelect
                  label="Select Service"
                  name="service"
                  options={SERVICE_OPTIONS}
                  placeholder="Choose a service"
                  value={formData.service}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.service}
                  touched={touched.service}
                  required
                />

                <div className="grid md:grid-cols-2 gap-4">
                  <FormInput
                    label="Preferred Date"
                    name="date"
                    type="date"
                    min={today}
                    value={formData.date}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.date}
                    touched={touched.date}
                    required
                    icon={Calendar}
                  />
                  <FormSelect
                    label="Preferred Time"
                    name="time"
                    options={TIME_SLOTS}
                    placeholder="Select a time slot"
                    value={formData.time}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.time}
                    touched={touched.time}
                    required
                  />
                </div>
              </div>

              {/* Additional Notes */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-soft border border-neutral-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-neutral-900">
                    Additional Information
                  </h2>
                </div>

                <FormTextarea
                  label="Notes (Optional)"
                  name="notes"
                  placeholder="Any additional information, medical history, or special requirements..."
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  helperText="Optional - Let us know if you have any specific requirements"
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={isSubmitting}
                  className="flex-1"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Submit Appointment Request
                </Button>
                <Button
                  type="button"
                  variant="whatsapp"
                  size="lg"
                  onClick={handleBookViaWhatsApp}
                  className="flex-1"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Book via WhatsApp
                </Button>
              </div>

              {/* Info Note */}
              <div className="bg-primary-50 rounded-xl p-4 flex items-start gap-3">
                <Clock className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-primary-800">
                  <p className="font-medium mb-1">Appointment Confirmation</p>
                  <p>
                    After submitting your request, our team will contact you within 
                    2-4 hours to confirm your appointment. For immediate assistance, 
                    please use WhatsApp or call us at{' '}
                    <a 
                      href={`tel:${CONTACT_INFO.phone}`}
                      className="font-semibold hover:underline"
                    >
                      {CONTACT_INFO.phoneDisplay}
                    </a>
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Alternatives */}
      <section className="py-12 bg-neutral-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl font-semibold text-neutral-900 text-center mb-8">
              Other Ways to Book
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <a
                href={`tel:${CONTACT_INFO.phone}`}
                className="flex items-center gap-4 p-6 bg-white rounded-xl shadow-soft hover:shadow-soft-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Phone className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-900">Call Us</p>
                  <p className="text-primary-600">{CONTACT_INFO.phoneDisplay}</p>
                </div>
              </a>
              <a
                href={CONTACT_INFO.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-6 bg-white rounded-xl shadow-soft hover:shadow-soft-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-900">WhatsApp</p>
                  <p className="text-green-600">Chat with us directly</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BookAppointmentPage;
